import { FCX } from "@datassential/platform-ui-lib";
import HomeHeader from "@pages/Home/components/HomeHeader/HomeHeader";
import { useEffect, useMemo } from "react";
import HomeSubHeader from "@pages/Home/components/HomeHeader/HomeSubHeader";
import SelectAmong from "@vms/SelectAmong";
import SelectDisplayMetric from "@vms/SelectDisplayMetric";
import { useDisplayMetricContext, useHomeContext, useHomeSearchSettingsContext } from "@pages/Home/HomeContext";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import { HomeGainDataItem, HomeGainRequestPayload, useHomeGainData } from "@api/useHomeGainData";
import { useFilteredKeywordsData } from "@api/useFilteredKeywordsData";
import { useFilterSort } from "@hooks/useFilterSort";
import { Sort } from "@models";
import { TableColumnsContext } from "@components/Table/TableColumns";
import WindowScrollerTable from "@components/Table/WindowScrollerTable";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { useMetric } from "@core/CommonDataContext";
import { CopyTableOptions, useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";
import { GainViewTableConfigProps, useGainViewTableConfig } from "@pages/Home/components/GainView/GainViewTableConfig";
import HomeActionsBar from "@pages/Home/components/HomeActionsBar/HomeActionsBar";
import { ProfileExportPptRequest } from "@pages/Profile/components/Header/Header";
import { HOME_GAIN_AFFINITY_ORDER, Metric } from "@models/Metric";

const GainView: FCX = () => {
    const { search } = useHomeContext();
    const { data: homeSearchData, resetSearch} = useHomeSearchSettingsContext();
    const { displayMetricId, setDisplayMetricId } = useDisplayMetricContext();
    const { amongPayload } = useSelectAmongContext();

    const payload = useMemo(
        (): HomeGainRequestPayload => ({
            ...amongPayload,
            metric: displayMetricId as HomeGainRequestPayload["metric"],
        }),
        [amongPayload, displayMetricId]
    );

    const { data: rawData, isLoading } = useHomeGainData(payload);

    const { getFinalData, dataForSorting } = useFilteredKeywordsData(rawData, search);

    const mappedDataForSorting = useMemo(
        () => dataForSorting.map(i => {
            const columns: { [key: string]: number | null } = {};
            i.columns.forEach((i, key) => {
                columns[`column${key}`] = i.sortValue;
            });

            return {
                ...i,
                ...columns,
            };
        }),
        [dataForSorting]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort(
        mappedDataForSorting,
        'name',
        Sort.Asc,
    );

    useEffect(() => {
        if (homeSearchData?.sortByAffinityAmong) {
            setSortingKey(`column${homeSearchData?.sortByAffinityAmong}` as keyof HomeGainDataItem);
            setSortingDirection(homeSearchData.sortingDirection);
        }
    }, [homeSearchData, setSortingDirection, setSortingKey]);

    const data = useMemo(
        () => getFinalData(sortedData),
        [getFinalData, sortedData]
    );

    const tableConfigProps = useMemo(
        (): GainViewTableConfigProps => ({
            data,
        }),
        [data]
    );

    const tableConfig = useGainViewTableConfig(tableConfigProps);

    const tableProviderValue = useMemo(
        () => ({
            data: tableConfig,
        }),
        [tableConfig],
    );

    const tableSortingProviderValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                { id: 'name', name: 'name', defaultDirection: Sort.Asc, },
                { id: 'macStage', name: 'macStage' },
                { id: 'base', name: 'base' },
                ...HOME_GAIN_AFFINITY_ORDER.map((amongId) => ({
                    id: `column${amongId}`,
                    name: `column${amongId}`,
                })),
                ...HOME_GAIN_AFFINITY_ORDER.map((amongId) => ({
                    id: `column${amongId}Change`,
                    name: `column${amongId}Change`,
                }))
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof HomeGainDataItem);
                setSortingDirection(direction);
            },
        }),
        [setSortingDirection, setSortingKey, sortingDirection, sortingKey]
    );

    const displayMetric = useMetric(displayMetricId);

    const copyTableOptions = useMemo(
        (): CopyTableOptions => ({
            additionalRows: [[`Metric: ${displayMetric ? displayMetric?.name : 'Base'}`]]
        }),
        [displayMetric]
    );

    const copyTableContent = useCopyTableDataWithFilters(tableConfig, data.length, copyTableOptions);

    const exportPayload = useMemo(
        (): ProfileExportPptRequest => {
            if (displayMetricId === null) {
                return {};
            }

            return { trendMetric: displayMetricId as ProfileExportPptRequest['trendMetric'] };
        },
        [displayMetricId]
    );

    useEffect(() => {
        setDisplayMetricId(Metric.LoveLikeIt);
    }, [setDisplayMetricId]);

    useEffect(() => {
        if (homeSearchData?.displayMetric) {
            setDisplayMetricId(homeSearchData.displayMetric);
        }
    }, [homeSearchData, setDisplayMetricId])

    return (
        <>
            <HomeHeader resultCount={dataForSorting.length} isLoading={isLoading}/>
            <HomeSubHeader>
                <SelectDisplayMetric
                    value={displayMetricId}
                    setValue={setDisplayMetricId}
                    hideBase
                    hidePolarity
                    onChange={resetSearch}
                />
                <SelectAmong
                    label="AMONG:"
                    useGroupsLimit
                    useCoExclusions
                    onApply={resetSearch}
                />
            </HomeSubHeader>
            <TableSortingContext.Provider value={tableSortingProviderValue}>
                <TableColumnsContext.Provider value={tableProviderValue}>
                    <WindowScrollerTable
                        className={"HomeGainView__table"}
                        rowsCount={isLoading ? 0 : data.length}
                        headHeight={80}
                        rowHeight={42}
                        isLoading={isLoading}
                        showFallback
                    >
                        <AsideButtons>
                            <CopyTableButton
                                data={copyTableContent}
                            />
                        </AsideButtons>
                    </WindowScrollerTable>
                </TableColumnsContext.Provider>
            </TableSortingContext.Provider>
            <HomeActionsBar exportPayload={exportPayload}/>
        </>
    );
};

export default GainView;